import { Button } from "@svea/design-system-components-react";
import {
	ButtonGroup,
	Dialog,
	type FieldOption,
	formatPhone,
	isRequired,
	RadioGroupField,
	ReactSelectField,
	Stack,
	Text,
	TextField,
	useText,
	validateCardPhoneNumber,
	validateEmail,
} from "@sveabank/cc-common";
import { Field, Form } from "react-final-form";
import { usePageContext } from "../../../../hooks/context";
import type {
	ClcDebitCard,
	ClcDebitCardConfigForm,
	ClcPerson,
} from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";

type Props = {
	person: ClcPerson;
	isOpen: boolean;
	setOpen: (isOpen: boolean) => void;
	cardId?: string;
};

export const ClcConfigCardModal = ({ person, isOpen, setOpen, cardId }: Props) => {
	const [t, texts] = useText();

	const {
		configureClcDebitCard,
		cards: {
			debitCards: { availableCardHolders, cards: allCards },
		},
		selectedPackage: { products },
		countryCodes,
	} = usePageContext(useClcCompanyApplication);

	const existingCard = allCards.find((c) => c.productId === cardId);
	const transactionAccountId = products.find(
		(p) => p.productDefinitionId === "TRANSAKTIONSKONTO",
	)?.productId;

	if (!transactionAccountId) throw new Error("Unexpected: no transaction account found");

	const nameSuggestionOptions: Array<FieldOption> =
		availableCardHolders
			.find((c) => c.nationalId === person.nationalId)
			?.displayNames.map((d) => ({ label: d, value: d })) || [];

	const initialValues: Partial<ClcDebitCardConfigForm> = {
		productId: existingCard?.productId,
		nationalId: person.nationalId,
		accountId: existingCard?.selectedAccountId || transactionAccountId,
		displayName: existingCard?.displayName || nameSuggestionOptions[0]?.value || undefined,
		email: existingCard?.email || person.email || undefined,
		phone: existingCard?.phone || person.phone || undefined,
	};

	const onSubmit = async (values: ClcDebitCardConfigForm) => {
		const formattedValues: ClcDebitCardConfigForm = {
			...values,
			phone: {
				...values.phone,
				number: formatPhone(values.phone.number),
			},
		};
		await configureClcDebitCard(formattedValues);
		setOpen(false);
	};

	const validateUniqueCardEmail = (allCards: Array<ClcDebitCard>) => (email: string) =>
		allCards.filter((c) => c.nationalId !== person.nationalId).some((c) => c.email === email)
			? t(texts.newCompany.products.configCardEmailNotUnique)
			: undefined;

	return (
		<Dialog
			isOpen={isOpen}
			setOpen={setOpen}
			size="small"
			center
			heading={t(texts.newCompany.products.configCardModalHeading)}
		>
			<Dialog.Content>
				<Form<ClcDebitCardConfigForm>
					onSubmit={onSubmit}
					initialValues={initialValues}
					render={({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit}>
							<Stack gap={4}>
								<Text align="start">{t(texts.newCompany.products.configCardModalText1)}</Text>
								<RadioGroupField name="displayName" options={nameSuggestionOptions} />
								<Stack direction="row" align="start" gap={2}>
									<ReactSelectField
										isDisabled={!!person.phone?.countryCode}
										options={countryCodes}
										name="phone.countryCode"
										initialValue={initialValues.phone?.countryCode || "+46"}
										label={t(texts.contactInfo.applicantContact.countryCode)}
										floatingLabel
										menuPortalTarget={null}
										validators={[isRequired]}
									/>
									<TextField
										disabled={!!person.phone?.number}
										name="phone.number"
										label={t(texts.contactInfo.applicantContact.phoneNumber)}
										validators={[isRequired, validateCardPhoneNumber]}
									/>
								</Stack>
								<TextField
									disabled={!!person.email}
									name="email"
									label={t(texts.common.email)}
									validators={[isRequired, validateEmail, validateUniqueCardEmail(allCards)]}
								/>
								<Field name="productId" render={() => <input type="hidden" />} />
								<Field name="nationalId" render={() => <input type="hidden" />} />
								<Field name="accountId" render={() => <input type="hidden" />} />
								<Text align="start">{t(texts.newCompany.products.configCardModalText2)}</Text>
								<ButtonGroup>
									<Button type="submit" isPending={submitting}>
										{t(texts.common.save)}
									</Button>
									<Button variant="secondary" onPress={() => setOpen(false)}>
										{t(texts.common.close)}
									</Button>
								</ButtonGroup>
							</Stack>
						</form>
					)}
				/>
			</Dialog.Content>
		</Dialog>
	);
};
